import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { useExamReport } from '../../school_panel/api/hooks';
import { EmptyState, ShowMoreFooter, Spinner, Track } from '../../school_panel/components/ui';

const PREVIEW_ROWS = 10;

/**
 * Statistics tab of the exam detail page: per-question % correct, read from the
 * exam report. Mounted only while the tab is open, so the report loads on demand.
 */
export default function ExamQuestionStats({ examId }: { examId: number }) {
  const { data, isLoading, isError } = useExamReport(examId, { section: 'all' });
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return <div className="flex justify-center py-10"><Spinner size="md" className="text-muted" /></div>;
  }
  if (isError || !data) {
    return <p role="alert" className="rounded-[12px] bg-danger-bg px-4 py-3 text-sm text-danger-ink">Could not load statistics for this exam.</p>;
  }
  if (data.header.took_count === 0) {
    return (
      <EmptyState compact icon={<BarChart3 size={18} strokeWidth={1.8} aria-hidden />}
        title="No results yet" body="Nobody has taken this exam yet." />
    );
  }

  const pass = data.header.pass_score_pct;
  const allRows = [...data.questions].sort((a, b) => a.order - b.order);
  const rows = expanded ? allRows : allRows.slice(0, PREVIEW_ROWS);
  return (
    <div>
      <p className="mb-4 text-muted">
        How many of the {data.header.took_count} students who took the exam answered each question correctly. Orange is below the {pass}% pass mark.
      </p>
      <ul>
        {rows.map((q) => {
          const below = q.pct_correct != null && q.pct_correct < pass;
          return (
            <li key={q.question_id} className="flex items-center gap-3.5 py-2">
              <span className="w-10 flex-none font-mono text-[13px] font-semibold text-muted">Q{q.order}</span>
              <span className="hidden min-w-0 flex-[0_1_280px] truncate text-[13px] text-ink sm:block" title={q.excerpt}>{q.excerpt}</span>
              <Track className="min-w-0 flex-1" value={q.pct_correct ?? 0} tone={below ? 'accent' : 'slate'}
                label={`Question ${q.order} percent correct`} />
              <span className="w-[52px] flex-none text-right font-mono text-sm font-medium text-ink">
                {q.pct_correct == null ? '—' : `${Math.round(q.pct_correct)}%`}
              </span>
            </li>
          );
        })}
      </ul>
      <ShowMoreFooter className="mt-2" shown={rows.length} total={allRows.length} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4">
        <span className="text-[13px] text-muted">Item analysis, chapter breakdown and student results live in the report.</span>
        <Link to={`/exams/report/${examId}`}
          className="inline-flex min-h-[44px] items-center justify-center rounded-[12px] border border-navy bg-navy px-4 py-2 text-sm font-semibold text-white transition hover:bg-navy-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
          Open report
        </Link>
      </div>
    </div>
  );
}
